/**
 * Safe Markdown subset renderer for newsletter bodies.
 *
 * Two hard rules, both of which exist because a newsletter is attacker-supplied
 * content that arrives over a socket:
 *
 * 1. **No raw HTML, ever.** The renderer never passes source through; it
 *    tokenises the raw string and escapes every text run at emit time. There is
 *    no code path where source characters reach the DOM unescaped.
 *
 * 2. **No remote subresources.** Images must be `data:` URIs carried inside the
 *    signed event. A remote `<img>` would be a tracking pixel: it would tell the
 *    sender that this reader opened the message and leak their IP -- destroying
 *    the anonymity that makes anonymous_ok channels worth having (HP-02). It
 *    would also mean the rendered newsletter is not what was signed, since the
 *    image could be swapped afterwards. Inline data URIs are covered by the
 *    signature, so what you see is provably what the sender signed.
 *
 * The relay additionally sends a Content-Security-Policy that blocks remote
 * loads, so this is defence in depth rather than the only guard.
 */

/**
 * User-facing wording for the one case the renderer must show rather than hide.
 * Replaceable so the renderer stays language-agnostic; `setStrings` is how a UI
 * localises it.
 */
const STRINGS = {
  blockedImageTitle: 'Image from another server was blocked',
  blockedImage: (alt) => `[blocked image: ${alt || 'no description'}]`,
};

export function setStrings(overrides) {
  Object.assign(STRINGS, overrides);
}

const INLINE = /`([^`]+)`|!\[([^\]]*)\]\(([^)\s]+)\)|\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*\n]+)\*/g;

function esc(text) {
  return String(text).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

/** Links: https, mailto and herald only. No http (downgrade), no javascript, no data. */
export function safeHref(url) {
  const value = String(url).trim();
  return /^(https:\/\/|mailto:|herald:)/i.test(value) ? value : null;
}

/**
 * Images: base64 data URIs only, and not SVG -- an SVG can carry script and
 * would reintroduce the very thing rule 1 exists to prevent.
 */
export function safeImageSrc(url) {
  const value = String(url).trim();
  return /^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/]+={0,2}$/i.test(value) ? value : null;
}

function renderInline(raw) {
  let out = '';
  let last = 0;
  INLINE.lastIndex = 0;
  let match;
  while ((match = INLINE.exec(raw)) !== null) {
    out += esc(raw.slice(last, match.index));
    const [, code, imgAlt, imgSrc, linkText, linkHref, bold, italic] = match;

    if (code !== undefined) {
      out += `<code>${esc(code)}</code>`;
    } else if (imgSrc !== undefined) {
      const src = safeImageSrc(imgSrc);
      out += src
        ? `<img src="${esc(src)}" alt="${esc(imgAlt)}" loading="lazy">`
        // Refused images are surfaced, not silently dropped: a reader should
        // know the sender tried to load something from elsewhere. The wording
        // is supplied by the caller, since this module is UI-language agnostic.
        : `<span class="md-blocked" title="${esc(STRINGS.blockedImageTitle)}">${
          esc(STRINGS.blockedImage(imgAlt))}</span>`;
    } else if (linkHref !== undefined) {
      const href = safeHref(linkHref);
      out += href
        ? `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer nofollow">${esc(linkText)}</a>`
        : esc(linkText);
    } else if (bold !== undefined) {
      out += `<strong>${esc(bold)}</strong>`;
    } else if (italic !== undefined) {
      out += `<em>${esc(italic)}</em>`;
    }
    last = match.index + match[0].length;
  }
  return out + esc(raw.slice(last));
}

/**
 * @param {string} source raw markdown from a signed event
 * @returns {string} HTML safe to assign to innerHTML
 */
export function renderMarkdown(source) {
  const lines = String(source ?? '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let list = null;       // 'ul' | 'ol'
  let paragraph = [];
  let fence = null;      // accumulating code fence lines

  const closeParagraph = () => {
    if (paragraph.length) {
      out.push(`<p>${paragraph.map(renderInline).join('<br>')}</p>`);
      paragraph = [];
    }
  };
  const closeList = () => {
    if (list) { out.push(`</${list}>`); list = null; }
  };
  const closeAll = () => { closeParagraph(); closeList(); };

  for (const line of lines) {
    if (fence !== null) {
      if (/^```/.test(line)) {
        out.push(`<pre><code>${esc(fence.join('\n'))}</code></pre>`);
        fence = null;
      } else fence.push(line);
      continue;
    }
    if (/^```/.test(line)) { closeAll(); fence = []; continue; }

    if (/^\s*$/.test(line)) { closeAll(); continue; }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      closeAll();
      const level = heading[1].length + 1; // h1 is the app's own title
      out.push(`<h${level}>${renderInline(heading[2])}</h${level}>`);
      continue;
    }

    if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) { closeAll(); out.push('<hr>'); continue; }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) { closeAll(); out.push(`<blockquote>${renderInline(quote[1])}</blockquote>`); continue; }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      const want = bullet ? 'ul' : 'ol';
      closeParagraph();
      if (list !== want) { closeList(); out.push(`<${want}>`); list = want; }
      out.push(`<li>${renderInline((bullet ?? numbered)[1])}</li>`);
      continue;
    }

    closeList();
    paragraph.push(line);
  }

  if (fence !== null) out.push(`<pre><code>${esc(fence.join('\n'))}</code></pre>`);
  closeAll();
  return out.join('\n');
}

/** First N characters of the plain-text meaning, for an inbox preview row. */
export function markdownSnippet(source, length = 140) {
  const text = String(source ?? '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[#>*`_~-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > length ? `${text.slice(0, length - 1)}…` : text;
}

/** Rendered byte size, so a composer can warn before the relay refuses. */
export function contentBytes(source) {
  return new TextEncoder().encode(String(source ?? '')).length;
}
